import { API_BASE, api } from "@/lib/apiClient";

export interface TuerwartungProjectSummary {
  project_code: string;
  project_raw?: string | null;
  project_location?: string | null;
  kunde?: string | null;
  visits_total?: number;
  visits_ok?: number;
  visits_issue?: number;
  visits_no_access?: number;
  protocols_total?: number;
  protocols_synced?: number;
  maintenance_entries_total?: number;
  maintenance_entries_with_protocol?: number;
  last_protocol_created_at?: string | null;
  doors_count?: number;
  last_maintenance?: string | null;
  doors_with_masterdata_count?: number;
  doors_without_masterdata_count?: number;
  assigned_project_label?: string | null;
  /** Nur in Live-Berechnung; Cache entfernt das Feld oft. */
  assigned_project_count?: number | null;
  assigned_customers?: string[];
  assigned_locations?: string[];
  assigned_years?: string[];
  [k: string]: unknown;
}

export interface DoorProtocolReport {
  id?: number;
  project_code?: string;
  door_number?: string;
  offpaper_submission_id?: number | null;
  report_created_at?: string | null;
  sharepoint_file_id?: string | null;
  sharepoint_web_url?: string | null;
  report_hash?: string | null;
  local_file_path?: string | null;
  created_by?: string | null;
  [k: string]: unknown;
}

export interface DoorProtocolStatusItem {
  door_number: string;
  has_report: boolean;
  latest_report?: DoorProtocolReport | null;
}

export interface DoorProtocolStatus {
  project_code: string;
  doors_total: number;
  doors_with_reports: number;
  has_missing_reports: boolean;
  show_generate_button: boolean;
  can_generate_missing: boolean;
  can_regenerate_all: boolean;
  protocols_folder_path?: string | null;
  protocols_folder_url?: string | null;
  note5_protocols_folder_path?: string | null;
  note5_protocols_folder_url?: string | null;
  items?: DoorProtocolStatusItem[];
}

export interface DoorProtocolGenerateRequest {
  force?: boolean;
  note5_only?: boolean;
}

export interface DoorProtocolGenerateStart {
  job_id: string;
  project_code: string;
  status: string;
}

export interface DoorProtocolJobSummary {
  doors_total?: number;
  processed_count?: number;
  generated_count?: number;
  skipped_count?: number;
  error_count?: number;
  skip_reasons?: Record<string, number>;
  [k: string]: unknown;
}

export interface DoorProtocolJobStatus {
  job_id?: string;
  project_code?: string;
  status?: string;
  total?: number;
  processed?: number;
  generated?: number;
  skipped?: number;
  errors?: number;
  current_door?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  summary?: DoorProtocolJobSummary | null;
  error?: string | null;
  message?: string | null;
  [k: string]: unknown;
}

export type DoorProtocolJobEventName = "started" | "progress" | "done" | "failed";

export interface DoorProtocolProgress {
  total: number;
  processed: number;
  generated: number;
  skipped: number;
  errors: number;
  current_door: string;
  state: "idle" | "running" | "done" | "failed";
  message: string;
  skip_reason_summary: string;
  summary: DoorProtocolJobSummary;
}

export function emptyDoorProtocolProgress(): DoorProtocolProgress {
  return {
    total: 0,
    processed: 0,
    generated: 0,
    skipped: 0,
    errors: 0,
    current_door: "",
    state: "idle",
    message: "",
    skip_reason_summary: "",
    summary: {},
  };
}

export async function listTuerwartungProjectsSummary(): Promise<TuerwartungProjectSummary[]> {
  const data = await api.get<TuerwartungProjectSummary[] | { items?: TuerwartungProjectSummary[] }>(
    "/tuerwartung/projects/summary",
  );
  if (Array.isArray(data)) return data;
  return Array.isArray(data?.items) ? data.items : [];
}

export async function getProjectDoorProtocolStatus(projectCode: string): Promise<DoorProtocolStatus> {
  return api.get<DoorProtocolStatus>(
    `/tuerwartung/projects/${encodeURIComponent(projectCode)}/door-protocols/status`,
  );
}

export async function startProjectDoorProtocolGeneration(
  projectCode: string,
  payload: DoorProtocolGenerateRequest = {},
): Promise<DoorProtocolGenerateStart> {
  return api.post<DoorProtocolGenerateStart>(
    `/tuerwartung/projects/${encodeURIComponent(projectCode)}/door-protocols/generate`,
    {
      force: Boolean(payload.force),
      note5_only: Boolean(payload.note5_only),
    },
  );
}

export interface AssignTuerwartungProjectResult {
  updated: number;
}

/** Offpaper-Wartungen eines project_code einem Auftrag (auftraege.id) zuordnen. */
export async function assignTuerwartungProject(
  projectCode: string,
  projectId: number | null,
): Promise<AssignTuerwartungProjectResult> {
  return api.patch<AssignTuerwartungProjectResult>(
    `/tuerwartung/projects/${encodeURIComponent(projectCode)}/assign-project`,
    { project_id: projectId },
  );
}

/** True, wenn in der Summary noch kein Auftrag zugeordnet ist. */
export function isUnassignedProjectSummaryRow(row: TuerwartungProjectSummary | null | undefined): boolean {
  if (!row) return false;
  const assignedProjectLabel = String(row.assigned_project_label || "").trim();
  const assignedProjectCountRaw = row.assigned_project_count;
  const assignedProjectCount = Number(assignedProjectCountRaw);
  const hasAssignedProjectCount =
    assignedProjectCountRaw !== undefined &&
    assignedProjectCountRaw !== null &&
    String(assignedProjectCountRaw) !== "";
  const isUnassignedByLabel = !assignedProjectLabel;
  const isUnassignedByCount =
    !hasAssignedProjectCount || Number.isNaN(assignedProjectCount) || assignedProjectCount <= 0;
  return isUnassignedByLabel && isUnassignedByCount;
}

export async function getProjectDoorProtocolJobStatus(
  projectCode: string,
  jobId: string,
): Promise<DoorProtocolJobStatus> {
  return api.get<DoorProtocolJobStatus>(
    `/tuerwartung/projects/${encodeURIComponent(projectCode)}/door-protocols/generate/${encodeURIComponent(jobId)}/status`,
  );
}

export function doorProtocolJobEventsUrl(projectCode: string, jobId: string): string {
  const path = `/tuerwartung/projects/${encodeURIComponent(projectCode)}/door-protocols/generate/${encodeURIComponent(jobId)}/events`;
  if (!API_BASE) return path;
  return `${API_BASE}${path}`;
}

export function formatSkipReasonSummary(
  skipReasons: Record<string, number> | null | undefined,
  maxItems = 3,
): string {
  if (!skipReasons || typeof skipReasons !== "object") return "";
  const entries = Object.entries(skipReasons)
    .map(([reason, count]) => ({
      reason: String(reason || "").trim(),
      count: Number(count || 0),
    }))
    .filter((item) => item.reason && Number.isFinite(item.count) && item.count > 0)
    .sort((a, b) => b.count - a.count);
  if (!entries.length) return "";
  return entries
    .slice(0, Math.max(maxItems, 1))
    .map((item) => `${item.reason}: ${item.count}`)
    .join(" · ");
}

export function shouldShowNote5DominantHint(
  note5Only: boolean,
  summary: DoorProtocolJobSummary | null | undefined,
): boolean {
  if (!note5Only) return false;
  const generatedCount = Number(summary?.generated_count || 0);
  if (generatedCount !== 0) return false;
  const skipReasons = summary?.skip_reasons && typeof summary.skip_reasons === "object" ? summary.skip_reasons : {};
  const targetReason = "Gefahrenbeurteilung ist nicht Note 5";
  const targetCount = Number(skipReasons[targetReason] || 0);
  const totalSkipped = Object.values(skipReasons)
    .map((count) => Number(count || 0))
    .filter((count) => Number.isFinite(count) && count > 0)
    .reduce((acc, count) => acc + count, 0);
  return targetCount > 0 && targetCount >= Math.ceil(totalSkipped / 2);
}

export function applyDoorProtocolJobEvent(
  eventName: DoorProtocolJobEventName | string,
  payload: DoorProtocolJobStatus,
  previous: DoorProtocolProgress = emptyDoorProtocolProgress(),
): DoorProtocolProgress {
  const next: DoorProtocolProgress = {
    ...previous,
    total: Number(payload.total ?? previous.total ?? 0),
    processed: Number(payload.processed ?? previous.processed ?? 0),
    generated: Number(payload.generated ?? previous.generated ?? 0),
    skipped: Number(payload.skipped ?? previous.skipped ?? 0),
    errors: Number(payload.errors ?? previous.errors ?? 0),
    current_door: String(payload.current_door || ""),
  };

  if (eventName === "started") {
    return {
      ...next,
      state: "running",
      message: "",
      skip_reason_summary: "",
      summary: {},
    };
  }

  if (eventName === "progress") {
    return {
      ...next,
      state: "running",
      message: "",
      skip_reason_summary: "",
    };
  }

  if (eventName === "done") {
    const summary = (payload.summary && typeof payload.summary === "object" ? payload.summary : {}) as DoorProtocolJobSummary;
    return {
      total: Number(summary.doors_total ?? next.total ?? 0),
      processed: Number(summary.processed_count ?? next.total ?? next.processed ?? 0),
      generated: Number(summary.generated_count ?? next.generated ?? 0),
      skipped: Number(summary.skipped_count ?? next.skipped ?? 0),
      errors: Number(summary.error_count ?? next.errors ?? 0),
      skip_reason_summary: formatSkipReasonSummary(summary.skip_reasons),
      summary,
      current_door: "",
      state: "done",
      message: "",
    };
  }

  if (eventName === "failed") {
    return {
      ...next,
      state: "failed",
      message: String(payload.error || payload.message || "Die Generierung wurde mit einem Fehler beendet."),
      current_door: "",
      skip_reason_summary: "",
    };
  }

  return next;
}

export async function watchDoorProtocolJobWithPolling(
  projectCode: string,
  jobId: string,
  onUpdate: (progress: DoorProtocolProgress) => void,
  options?: { signal?: AbortSignal; intervalMs?: number },
): Promise<DoorProtocolJobStatus> {
  const intervalMs = options?.intervalMs ?? 1500;
  let progress = emptyDoorProtocolProgress();

  while (!options?.signal?.aborted) {
    const status = await getProjectDoorProtocolJobStatus(projectCode, jobId);
    const eventName =
      status.status === "done" ? "done" : status.status === "failed" ? "failed" : "progress";
    progress = applyDoorProtocolJobEvent(eventName, status, progress);
    onUpdate(progress);

    if (status.status === "done" || status.status === "failed") {
      return status;
    }

    await new Promise<void>((resolve, reject) => {
      const timer = window.setTimeout(() => resolve(), intervalMs);
      options?.signal?.addEventListener(
        "abort",
        () => {
          window.clearTimeout(timer);
          reject(new DOMException("Aborted", "AbortError"));
        },
        { once: true },
      );
    });
  }

  throw new DOMException("Aborted", "AbortError");
}

/**
 * Folgt einem Protokoll-Job per SSE; bei Stream-Fehler Fallback auf Polling.
 */
export function watchDoorProtocolJob(
  projectCode: string,
  jobId: string,
  onUpdate: (progress: DoorProtocolProgress) => void,
  options?: { signal?: AbortSignal },
): Promise<DoorProtocolJobStatus> {
  if (typeof window === "undefined" || typeof window.EventSource === "undefined") {
    return watchDoorProtocolJobWithPolling(projectCode, jobId, onUpdate, options);
  }

  return new Promise((resolve, reject) => {
    let settled = false;
    let progress = emptyDoorProtocolProgress();
    const streamUrl = doorProtocolJobEventsUrl(projectCode, jobId);
    const source = new EventSource(streamUrl, { withCredentials: true });

    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      source.close();
      options?.signal?.removeEventListener("abort", onAbort);
      fn();
    };

    const onAbort = () => {
      finish(() => reject(new DOMException("Aborted", "AbortError")));
    };
    options?.signal?.addEventListener("abort", onAbort);

    const handleEvent = (eventName: DoorProtocolJobEventName) => (event: MessageEvent) => {
      let payload: DoorProtocolJobStatus = {};
      try {
        payload = event.data ? (JSON.parse(String(event.data)) as DoorProtocolJobStatus) : {};
      } catch {
        payload = {};
      }
      progress = applyDoorProtocolJobEvent(eventName, payload, progress);
      onUpdate(progress);
      if (eventName === "done") {
        finish(() => resolve({ ...payload, status: "done", summary: progress.summary }));
      } else if (eventName === "failed") {
        finish(() =>
          reject(new Error(progress.message || "Wartungsprotokoll-Generierung fehlgeschlagen.")),
        );
      }
    };

    (["started", "progress", "done", "failed"] as const).forEach((name) => {
      source.addEventListener(name, handleEvent(name) as EventListener);
    });

    source.addEventListener("error", () => {
      if (settled) return;
      settled = true;
      source.close();
      options?.signal?.removeEventListener("abort", onAbort);
      void watchDoorProtocolJobWithPolling(projectCode, jobId, onUpdate, options)
        .then(resolve)
        .catch(reject);
    });
  });
}

export function resolveProtocolMeta(
  summary: TuerwartungProjectSummary | null | undefined,
  status: DoorProtocolStatus | null | undefined,
) {
  const hasStatus = Boolean(status);
  const summaryTotal = Number(summary?.maintenance_entries_total ?? summary?.visits_total ?? 0);
  const summaryWithProtocol = Number(
    summary?.maintenance_entries_with_protocol ?? summary?.protocols_total ?? 0,
  );
  const statusTotal = Number(status?.doors_total ?? 0);
  const statusWithProtocol = Number(status?.doors_with_reports ?? 0);

  const maintenanceEntriesTotal = hasStatus ? statusTotal : summaryTotal;
  const maintenanceEntriesWithProtocol = hasStatus ? statusWithProtocol : summaryWithProtocol;
  const maintenanceEntriesMissingProtocol = Math.max(
    maintenanceEntriesTotal - maintenanceEntriesWithProtocol,
    0,
  );
  const lastProtocolCreatedAt = String(summary?.last_protocol_created_at || "");
  const protocolCoverageComplete =
    maintenanceEntriesTotal > 0 && maintenanceEntriesWithProtocol >= maintenanceEntriesTotal;
  const canGenerateMissing = hasStatus
    ? Boolean(status?.can_generate_missing)
    : maintenanceEntriesMissingProtocol > 0;
  const canRegenerateAll = hasStatus ? Boolean(status?.can_regenerate_all) : maintenanceEntriesTotal > 0;

  return {
    maintenanceEntriesTotal,
    maintenanceEntriesWithProtocol,
    maintenanceEntriesMissingProtocol,
    lastProtocolCreatedAt,
    protocolCoverageComplete,
    canGenerateMissing,
    canRegenerateAll,
  };
}

export function projectFilterLabel(row: TuerwartungProjectSummary): string {
  const code = String(row.project_code || "").trim();
  const assigned = String(row.assigned_project_label || "").trim();
  if (assigned && assigned !== code) return `${code} · ${assigned}`;
  return code || String(row.project_raw || "").trim() || "–";
}
